// Typed API client for the seatmap backend (`/seatmap_api`).
//
// All HTTP calls flow through `apiFetch` so that 401 responses uniformly
// clear sessionStorage and reload the app. Response shapes are typed at the
// boundary; callers no longer have to know about field-name fallbacks
// (the previous `data.guestGuid || data.guid || data.id || ''` pattern is
// centralised in `parseGuestResponse`).

import type {
  GroupContext,
  RoomData,
  SeatToGuestMap,
} from '../types';
import { apiFetch, SessionExpiredError } from '../utils/apiFetch';

export interface LoginResponse {
  roomData: RoomData;
  seatToGuestMap: SeatToGuestMap;
  sessionId: string;
  eventId?: string;
  extensionMode?: boolean;
  reservationGroupId?: string;
}

export interface ValidatedGuest {
  guestGuid: string;
  guestCode: string;
}

/**
 * Thrown when the backend returns 409 from POST `/seatmap_api/seatmap/assignments/{sessionId}`,
 * meaning a concurrent update changed the same seat (optimistic-locking conflict).
 * Callers should refetch the seatmap and re-attempt.
 */
export class SeatConflictError extends Error {
  constructor() {
    super('seatConflict');
    this.name = 'SeatConflictError';
  }
}

/** Tolerant parse for the various field-name variants the guest endpoint has used. */
function parseGuestResponse(raw: unknown, fallbackCode: string): ValidatedGuest {
  const r = (raw ?? {}) as Record<string, unknown>;
  const code = (r.guestCode ?? r.code ?? r.guest_code ?? r.guestcode ?? r.name ?? fallbackCode) as string;
  const guid = (r.guestGuid ?? r.guid ?? r.guest_guid ?? r.id ?? '') as string;
  return { guestCode: code, guestGuid: guid };
}

export class SeatmapApiClient {
  /** Log in with a booking code; throws on non-2xx with the parsed error message. */
  async login(bookingCode: string): Promise<LoginResponse> {
    const res = await apiFetch(`seatmap/${encodeURIComponent(bookingCode)}`);
    if (!res.ok) {
      // Pull a friendly error message if the backend supplied one.
      let body: { error?: string; message?: string } | undefined;
      try { body = await res.json(); } catch { /* not JSON */ }
      const msg = body?.error || body?.message
        || (res.status === 404 ? 'invalidBookingCode' : 'loginError');
      const err = new Error(msg);
      (err as Error & { status?: number }).status = res.status;
      throw err;
    }
    return res.json() as Promise<LoginResponse>;
  }

  /** Returns true if the backend session is still valid. */
  async validateSession(sessionId: string): Promise<boolean> {
    try {
      const res = await apiFetch(`sessions/${encodeURIComponent(sessionId)}`);
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Validate a single guest booking code; returns null on 404, throws on other errors. */
  async validateGuest(code: string): Promise<ValidatedGuest | null> {
    const res = await apiFetch(`guests/${encodeURIComponent(code)}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`validateGuest failed: ${res.status}`);
    const raw = await res.json();
    return parseGuestResponse(raw, code);
  }

  /** Fetch the reservation group context for the given booking code, if any. */
  async fetchGroupContext(bookingCode: string): Promise<GroupContext | null> {
    const res = await apiFetch(`groups/by-booking-code/${encodeURIComponent(bookingCode)}`);
    if (!res.ok) return null;
    return res.json() as Promise<GroupContext>;
  }

  /** Persist a tentative or confirmed seat assignments map. */
  async submitAssignments(
    sessionId: string,
    assignments: Record<string, string>,
    confirmed: boolean,
  ): Promise<{ ok: boolean; status: number; map?: SeatToGuestMap }> {
    const url = `seatmap/assignments/${encodeURIComponent(sessionId)}${confirmed ? '?confirmed=true' : ''}`;
    const res = await apiFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(assignments),
    });
    if (res.status === 200) {
      const map = (await res.json()) as SeatToGuestMap;
      return { ok: true, status: 200, map };
    }
    if (res.status === 409) {
      // Optimistic-locking conflict: surface as a typed error so the UI can
      // toast "seat taken — refresh" without callers having to inspect status.
      throw new SeatConflictError();
    }
    return { ok: false, status: res.status };
  }

  /** Re-fetch the live seatmap (used after a STOMP push). */
  async fetchSeatToGuestMap(sessionId: string): Promise<SeatToGuestMap | null> {
    try {
      const res = await apiFetch(`seatmap/seatToGuestMap/${encodeURIComponent(sessionId)}`);
      if (!res.ok) return null;
      return res.json() as Promise<SeatToGuestMap>;
    } catch (e) {
      // A SessionExpiredError here means a background STOMP-triggered refetch hit a
      // 401. Swallow it and return null so the reload (already scheduled by apiFetch)
      // is not triggered mid-confirmation by a read-only background call.
      if (e instanceof SessionExpiredError) return null;
      throw e;
    }
  }
}

/** Singleton instance for app-wide use. */
export const seatmapApi = new SeatmapApiClient();
